import * as yup from 'yup'

import type { Rules } from './types'
import { FormFields } from './types'

export const validationSchema = yup.object().shape({
  [FormFields.text]: yup.string().max(30).required(),
  [FormFields.emoji]: yup.mixed().required(),
  [FormFields.borderColor]: yup.string(),
  [FormFields.backgroundColor]: yup.string(),
  [FormFields.textColor]: yup.string(),
  [FormFields.canBeEarnedByMembers]: yup
    .boolean()
    .when([FormFields.giveToSelectedMembers, FormFields.giveToSelectedTeamStaff], {
      is: (giveToSelectedMembers: boolean, giveToSelectedTeamStaff: boolean) =>
        giveToSelectedMembers === false && giveToSelectedTeamStaff === false,
      then: yup
        .boolean()
        .required()
        .oneOf([true], 'At least one rule or a member or internal team staff is required.'),
    }),
  [FormFields.removeWhenRulesNoLongerMet]: yup.boolean(),
  [FormFields.giveToSelectedMembers]: yup.boolean(),
  [FormFields.giveToSelectedTeamStaff]: yup.boolean(),
  [FormFields.rules]: yup
    .array()
    .when(FormFields.canBeEarnedByMembers, {
      is: (canBeEarnedByMembers: boolean) => canBeEarnedByMembers,
      then: yup.array().min(1, 'At least one rule is required.'),
    })
    .test('hasOnlyEmpty', 'At least one rule is required.', (value) => {
      if (!value) return true

      if (value.length === 1 && value[0].type === 'EmptyRule') return false
      if (value.length === 1 && value[0].type === 'Tier' && !value[0].tierId) return false

      return true
    })
    .test(
      'tierAndActiveMemberConflict',
      'Being a member of a tier rule cannot be combined with Member active rule.',
      (value) => {
        if (!value) return true

        const hasTierRuleWithId = value.some((rule) => rule.type === 'Tier' && rule.tierId)
        const hasActiveMemberRule = value.some((rule) => rule.type === 'ActiveMemberAt')

        if (hasTierRuleWithId && hasActiveMemberRule) {
          return false
        }

        return true
      },
    ),
  [FormFields.manuallyAssignedSupporters]: yup.array().when(FormFields.giveToSelectedMembers, {
    is: (giveToSelectedMembers: boolean) => giveToSelectedMembers,
    then: yup.array().min(1, 'At least one member is required.'),
  }),
  [FormFields.manuallyAssignedTeamStaff]: yup
    .array()
    .when(
      [FormFields.giveToSelectedTeamStaff, FormFields.verifiedApplyToType, FormFields.badgeType],
      {
        is: (giveToSelectedTeamStaff: boolean, verifiedApplyToType: string, badgeType: string) =>
          badgeType !== 'Verified' && giveToSelectedTeamStaff,
        then: yup.array().min(1, 'At least one team staff is required.'),
      },
    )
    .when(
      [FormFields.giveToSelectedTeamStaff, FormFields.verifiedApplyToType, FormFields.badgeType],
      {
        is: (giveToSelectedTeamStaff: boolean, verifiedApplyToType: string, badgeType: string) =>
          badgeType === 'Verified' && verifiedApplyToType === 'Selected',
        then: yup.array().min(1, 'At least one team staff is required.'),
      },
    ),
  [FormFields.commentsWrittenMin]: yup.number().when(FormFields.rules, {
    is: (rules: Rules) => rules.some((rule) => rule.type === 'CommentsWritten'),
    then: yup.number().required().min(1),
  }),
  [FormFields.commentsWrittenMax]: yup.number(),
  [FormFields.likesGivenMin]: yup.number().when(FormFields.rules, {
    is: (rules: Rules) => rules.some((rule) => rule.type === 'LikesGiven'),
    then: yup.number().required().min(1),
  }),
  [FormFields.likesGivenMax]: yup.number(),
  [FormFields.gifsGivenMin]: yup.number().when(FormFields.rules, {
    is: (rules: Rules) => rules.some((rule) => rule.type === 'GiftsGiven'),
    then: yup.number().required().min(1),
  }),
  [FormFields.gifsGivenMax]: yup.number(),
  [FormFields.firstSubscribedAtFrom]: yup.date(),
  [FormFields.firstSubscribedAtTo]: yup
    .date()
    .when([FormFields.firstSubscribedAtFrom, FormFields.rules], {
      is: (firstSubscribedAtFrom: string | undefined, rules: Rules) =>
        !firstSubscribedAtFrom && rules.some((rule) => rule.type === 'FirstSubscribedAt'),
      then: yup.date().required('At least one date needs to be set'),
    }),
  [FormFields.activeMemberAtFrom]: yup.date(),
  [FormFields.activeMemberAtTo]: yup
    .date()
    .when([FormFields.activeMemberAtFrom, FormFields.rules], {
      is: (activeMemberAtFrom: string | undefined, rules: Rules) =>
        !activeMemberAtFrom && rules.some((rule) => rule.type === 'ActiveMemberAt'),
      then: yup.date().required('At least one date needs to be set'),
    }),
  [FormFields.likesReceivedMin]: yup.number().when(FormFields.rules, {
    is: (rules: Rules) => rules.some((rule) => rule.type === 'LikesReceived'),
    then: yup.number().required().min(1),
  }),
  [FormFields.likesReceivedMax]: yup.number(),
})
